import { IUtilityContract, UtilitySource } from "@/api/facilities";
import ActionsPopover from "@/components/ActionsPopOver/ActionsPopOver";
import ActionsPopOverItem from "@/components/ActionsPopOver/ActionsPopOverItem";
import EstimateIcon from "@/components/icons/EstimateIcon";
import CrossReference from "@/components/links/CrossReference/CrossReference";
import useAnalytics from "@/hooks/useAnalytics";
import { useDeleteContract } from "@/hooks/useFacilities";
import usePublishedId from "@/pages/facilities/hooks/usePublishedId";
import { DeleteOutlined, EditOutlined, InfoCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Space, Spin, Tooltip } from "antd";
import dayjs from "dayjs";
import _ from "lodash";
import styles from "../FacilityDetail.module.scss";

export default function UtilityContract({
  facilityId,
  contract,
  sourceLabels,
  editContract,
  showRange,
  companyName,
  modalUpload,
}: {
  facilityId: number;
  contract: IUtilityContract;
  sourceLabels: (string | undefined)[];
  editContract: (contract: IUtilityContract) => void;
  showRange?: boolean;
  companyName?: string;
  modalUpload: boolean;
}) {
  const publishedId = usePublishedId();
  const deleteContract = useDeleteContract();
  const analytics = useAnalytics({ enabled: !publishedId });
  const sources: Array<(typeof contract.sources)[number]> = contract.sources;

  const sourceRow =
    contract.type === "residual_waste" ? (
      <>
        <td
          onClick={() => editContract(contract)}
          className={styles["first-col"]}
        >
          {contract.sources[0].label}
        </td>
        <td onClick={() => editContract(contract)}>
          <Space>
            <span>{contract.sources[0].amount}</span>
            <span className={styles["unit"]}>
              {contract.sources[0].unit}
              {contract.contract_type === "estimate" ? " / kg" : ""}
            </span>
          </Space>
        </td>
        <td onClick={() => editContract(contract)}>{contract.sources[0].activity_name}</td>
      </>
    ) : contract.type === "consumable" ? (
      <>
        <td
          onClick={() => editContract(contract)}
          className={styles["first-col"]}
        >
          {publishedId ? (
            contract.sources[0].name
          ) : (
            <CrossReference to={`/consumables/${contract.sources[0].id}`}>
              {contract.sources[0].name}
            </CrossReference>
          )}
        </td>
        <td onClick={() => editContract(contract)}>
          <Space>
            <span>{contract.sources[0].amount}</span>
            <span className={styles["unit"]}>{contract.sources[0].unit}</span>
          </Space>
        </td>
      </>
    ) : contract.type === "goods_and_services" ? (
      <>
        <td
          onClick={() => editContract(contract)}
          className={styles["first-col"]}
        >
          {publishedId ? (
            contract.sources[0].name
          ) : (
            <CrossReference to={`/goods_and_services/${contract.sources[0].id}`}>
              {contract.sources[0].name}
            </CrossReference>
          )}
        </td>
        <td onClick={() => editContract(contract)}>
          <Space>
            <span>{contract.sources[0].amount}</span>
            <span className={styles["unit"]}>{contract.sources[0].unit}</span>
          </Space>
        </td>
      </>
    ) : contract.type === "packaging" ? (
      <>
        <td className={styles["first-col"]}>
          {publishedId ? (
            contract.sources[0].name
          ) : (
            <CrossReference to={`/matching/packaging/materials/${contract.sources[0].id}`}>
              {contract.sources[0].name}
            </CrossReference>
          )}
        </td>
        <td>
          <Space>
            <span>{contract.sources[0].amount}</span>
            <span className={styles["unit"]}>{contract.sources[0].unit}</span>
          </Space>
        </td>
        <td>
          <span>{contract.sources[0].supplier_name}</span>
        </td>
        <td>
          <span>{contract.sources[0].supplier_address}</span>
        </td>
      </>
    ) : contract.type === "heat" ||
      contract.type === "water" ||
      contract.type === "electricity" ||
      contract.type === "cooling" ? (
      sourceLabels.map((sourceLabel) => {
        const source = sources.find((contractSource) => contractSource.label === sourceLabel) as
          | UtilitySource
          | undefined;

        if (!source) return <td></td>;

        return (
          <td
            key={source.urn}
            className={styles.source}
            onClick={() => editContract(contract)}
          >
            <Tooltip
              title={
                source.details && (
                  <>
                    <strong>Advanced source info:</strong> {source.details}
                  </>
                )
              }
            >
              <Space>
                <span>{_.round(source.amount * source.percentage, 2)}</span>
                <span className={styles["unit"]}>
                  {source.unit}
                  {contract.contract_type === "estimate" ? " / kg" : ""}
                </span>
              </Space>
              {source?.details && <InfoCircleOutlined style={{ marginLeft: 8 }} />}
            </Tooltip>
          </td>
        );
      })
    ) : (
      <></>
    );

  return (
    <tr className={modalUpload ? styles.withAction : undefined}>
      {showRange && (
        <td
          onClick={() => editContract(contract)}
          className={styles["first-col"]}
        >
          {dayjs(contract.start_date).format("MMM YY")} →{" "}
          {dayjs(contract.end_date).format("MMM YY")}
        </td>
      )}
      {sourceRow}
      {![
        "residual_waste",
        "capital_goods",
        "consumable",
        "packaging",
        "goods_and_services",
      ].includes(contract.type) && (
        <td
          className={styles.estimate}
          onClick={() => editContract(contract)}
        >
          <Tooltip title="Estimate">
            <>{contract.contract_type === "estimate" ? <EstimateIcon /> : <></>}</>
          </Tooltip>
        </td>
      )}
      <td onClick={() => editContract(contract)}>
        {contract.last_updated_by === "Company" ? companyName : "External"}
      </td>
      <td className={styles.actions}>
        <ActionsPopover
          trigger={["hover"]}
          actions={
            <>
              {modalUpload && (
                <ActionsPopOverItem
                  icon={<EditOutlined />}
                  onClick={() => editContract(contract)}
                >
                  Edit
                </ActionsPopOverItem>
              )}
              <ActionsPopOverItem
                icon={<DeleteOutlined />}
                danger
                onClick={() => {
                  deleteContract
                    .mutateAsync({ contractId: contract.id, utilityId: facilityId })
                    .then((result) => {
                      if (result.status === 200) {
                        analytics.track("Contract Deleted");
                      }
                    });
                }}
              >
                Remove
              </ActionsPopOverItem>
            </>
          }
        >
          <>
            {deleteContract.isLoading ? (
              <Spin size="small" />
            ) : (
              <MoreOutlined className={styles["action-button"]} />
            )}
          </>
        </ActionsPopover>
      </td>
    </tr>
  );
}
